import { useState, useCallback } from "react";
import { API } from "aws-amplify";
import { CustomerOptions } from "./useCustomerOptions";
import {
    getUniqueName,
    getDateOfBirth,
} from "../../../utils/RandomDemographicInfoGen";

/**
 * Customer creation payload
 */
export interface CustomerCreationPayload {
    emailPrefix?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    gender?: string;
    residentialAddressId?: string | null;
    options: CustomerOptions;
}

/**
 * Customer creation response
 */
export interface CustomerCreationResponse {
    // Login credentials for the created test customer (top-level in the create response).
    email?: string;
    password?: string;
    customerId?: string;
    profile?: Array<{
        personId: string;
        profileId?: string;
        phoneNumber?: string;
        demographicProfile?: {
            legalFirstName: string;
            legalLastName: string;
            dateOfBirth: string;
            gender: string;
        };
    }>;
    phoneNumber?: string;
}

/**
 * Customer address response
 */
export interface CustomerAddress {
    mailingAddress?: string;
    addressId?: string;
    postalCode?: string;
}

/**
 * Hook to manage customer creation workflow
 *
 * Handles:
 * - Customer enrollment API call
 * - Address fetching
 * - Loading and error states
 * - Response data management
 */
export const useCustomerCreation = () => {
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [enrollmentResponse, setEnrollmentResponse] = useState<CustomerCreationResponse | null>(null);
    const [error, setError] = useState<string>("");

    /**
     * Fetch customer addresses by personId
     */
    const getCustomerAddresses = useCallback(async (personId: string): Promise<CustomerAddress[]> => {
        const apiName = "ApaqPortalAPI";
        const path = `/customer/address/get-addresses?personId=${personId}`;

        const response = await API.get(apiName, path, {});
        return response.customerAddresses || [];
    }, []);

    /**
     * Get address ID for a specific zip code
     */
    const getAddressIdForZipCode = useCallback(async (zipCode: string): Promise<string | null> => {
        try {
            const apiName = "ApaqPortalAPI";
            const path = "/address/get-addresses";
            const response = await API.get(apiName, path, {});

            if (response.addresses && response.addresses.length > 0) {
                const matchingAddress = response.addresses.find(
                    (addr: any) => addr.postalCode === zipCode
                );
                if (matchingAddress) {
                    return matchingAddress.addressId;
                }
            }
            return null;
        } catch (error) {
            console.warn("Failed to fetch address ID for zip code:", error);
            return null;
        }
    }, []);

    /**
     * Create a pharmacy customer
     */
    const createCustomer = useCallback(async (payload: CustomerCreationPayload): Promise<CustomerCreationResponse> => {
        setIsEnrolling(true);
        setError("");

        try {
            const apiName = "ApaqPortalAPI";
            const path = "/pharmacy-customer/create";

            // Generate random data if not provided
            const firstName = payload.firstName || getUniqueName();
            const lastName = payload.lastName || getUniqueName();
            const dateOfBirth = payload.dateOfBirth || getDateOfBirth(Math.random(), -1000);
            // Gender is always provided from options (defaults to "Male" in useCustomerOptions)
            const gender = payload.options.gender ?? payload.gender;

            const requestPayload: any = {
                actorSubType: "ADULT",
                creditCard: true,
                customerJourneyType: "sign-up-for-self",
                dateOfBirth: dateOfBirth,
                demographicProfile: true,
                gender: gender,
                identityValidationStatus: "VALIDATION_SUCCESS",
                identityValidationForKatara: true,
                legalFirstName: firstName,
                legalLastName: lastName,
                phoneNumber: payload.phoneNumber?.trim() || "",
                name: `${firstName} ${lastName}`,
                programName: "WOLFGANG",
                source: "MANUAL",
                prime: payload.options.prime,
                rxPassForceActive: payload.options.rxPassForceActive,
                rxPassSubscription: payload.options.rxPassSubscription,
                hornbillSubscription: payload.options.hornbillSubscription,
                setDefaultAddress: payload.options.setDefaultAddress,
                healthProfile: true,
                commonAllergies: payload.options.commonAllergies,
                commonHealthConditions: payload.options.commonHealthConditions,
                pregnantOrNursing: payload.options.pregnantOrNursing,
            };

            // Add optional fields
            if (payload.emailPrefix?.trim()) {
                requestPayload.emailPrefix = payload.emailPrefix.trim();
            }

            if (payload.residentialAddressId) {
                requestPayload.residentialAddressId = payload.residentialAddressId;
            }

            const response = await API.post(apiName, path, { body: requestPayload });

            setEnrollmentResponse(response);
            setIsEnrolling(false);
            return response;
        } catch (err) {
            const error = err as any;
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to create pharmacy customer";

            console.error("Error creating customer:", error);
            setError(errorMessage);
            setIsEnrolling(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Reset the hook state
     */
    const reset = useCallback(() => {
        setIsEnrolling(false);
        setEnrollmentResponse(null);
        setError("");
    }, []);

    return {
        isEnrolling,
        enrollmentResponse,
        error,
        createCustomer,
        getCustomerAddresses,
        getAddressIdForZipCode,
        reset,
    };
};
